import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/ui/BackButton';
import { workerCertificationService } from '../../services/workerCertificationService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import ExpiryWarningBanner from './components/ExpiryWarningBanner';
import AddCertificationModal from './components/AddCertificationModal';
import EditCertificationModal from './components/EditCertificationModal';
import { pdfExportService } from '../../services/pdfExportService';
import { toast } from 'sonner';

const CERT_TYPES = ['All Types', 'CSCS', 'IPAF', 'PASMA', 'First Aid', 'Plant Operator', 'Scaffold', 'Asbestos Awareness', 'Other'];
const EXPIRY_STATUSES = ['All', 'Expired', 'Expiring Soon', 'Valid'];

const StatusBadge = ({ expiryDate }) => {
  const { t } = useLanguage();
  const status = workerCertificationService?.getExpiryStatus(expiryDate);
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive">
        <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
        {t('certExpired')}
      </span>
    );
  }
  if (status === 'expiring_soon') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning/10 text-warning">
        <span className="w-1.5 h-1.5 rounded-full bg-warning" />
        {t('certExpiringSoon')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
      <span className="w-1.5 h-1.5 rounded-full bg-success" />
      {t('certValid')}
    </span>
  );
};

const WorkerCertificationsManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [certifications, setCertifications] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [fileUrlCache, setFileUrlCache] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All Types');
  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.error) throw companyResult.error;
      const comp = companyResult?.data;
      setCompany(comp);

      if (comp?.id) {
        const certsResult = await workerCertificationService?.getCertificationsForCompany(comp?.id);
        if (certsResult?.error) throw certsResult.error;
        setCertifications(certsResult?.data || []);
      }
    } catch (err) {
      console.error('[WorkerCertifications] load certifications failed:', err);
      setError('Failed to load certifications. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (subscriptionReadOnly) return;
    if (!window.confirm('Are you sure you want to delete this certification record? This cannot be undone.')) return;
    try {
      setDeletingId(id);
      let result = await workerCertificationService?.deleteCertification(id);
      if (result?.error) throw result.error;
      setCertifications(prev => prev?.filter(c => c?.id !== id));
    } catch (err) {
      console.error('[WorkerCertifications] delete certification failed:', err);
      toast?.error(err?.message || 'Failed to delete certification. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewFile = async (cert) => {
    if (!cert?.file_path) return;
    if (fileUrlCache?.[cert?.id]) {
      window.open(fileUrlCache?.[cert?.id], '_blank');
      return;
    }
    let result = await workerCertificationService?.getCertificateFileUrl(cert?.file_path);
    if (result?.data) {
      setFileUrlCache(prev => ({ ...prev, [cert?.id]: result?.data }));
      window.open(result?.data, '_blank');
    }
  };

  const filteredCerts = useMemo(() => {
    let result = [...certifications];

    if (search) {
      const q = search?.toLowerCase();
      result = result?.filter(c =>
        c?.worker?.full_name?.toLowerCase()?.includes(q) ||
        c?.certificate_type?.toLowerCase()?.includes(q) ||
        c?.certificate_number?.toLowerCase()?.includes(q)
      );
    }

    if (filterType !== 'All Types') {
      result = result?.filter(c => c?.certificate_type === filterType);
    }

    if (filterStatus !== 'All') {
      result = result?.filter(c => {
        const status = workerCertificationService?.getExpiryStatus(c?.expiry_date);
        if (filterStatus === 'Expired') return status === 'expired';
        if (filterStatus === 'Expiring Soon') return status === 'expiring_soon';
        if (filterStatus === 'Valid') return status === 'valid';
        return true;
      });
    }

    return result;
  }, [certifications, search, filterType, filterStatus]);

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await pdfExportService?.generateWorkerCertificationsReport({
        certifications: filteredCerts,
        companyName: company?.company_name || '',
        workerName: undefined,
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast?.error('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const stats = useMemo(() => {
    const expired = certifications?.filter(c => workerCertificationService?.getExpiryStatus(c?.expiry_date) === 'expired')?.length || 0;
    const expiringSoon = certifications?.filter(c => workerCertificationService?.getExpiryStatus(c?.expiry_date) === 'expiring_soon')?.length || 0;
    const valid = certifications?.filter(c => workerCertificationService?.getExpiryStatus(c?.expiry_date) === 'valid')?.length || 0;
    return { total: certifications?.length, expired, expiringSoon, valid };
  }, [certifications]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <SubscriptionReadOnlyBanner className="mb-6" />

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <BackButton fallback="/admin-dashboard" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground font-heading">{t('workerCertificationsTitle')}</h1>
                <p className="text-muted-foreground mt-1">{t('workerCertificationsSubtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportPdf}
                  disabled={exportingPdf || filteredCerts?.length === 0}
                >
                  {exportingPdf ? t('exporting') : t('exportPdf')}
                </Button>
                <Button
                  variant="primary"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowAddModal(true)}
                  disabled={subscriptionReadOnly}
                >
                  {t('addCertification')}
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg p-4 border border-border shadow-elevation-1">
              <p className="text-2xl font-bold text-foreground font-heading">{stats?.total}</p>
              <p className="text-sm text-muted-foreground">{t('certTotal')}</p>
            </div>
            <div className="bg-card rounded-lg p-4 border border-destructive/20 shadow-elevation-1">
              <p className="text-2xl font-bold text-destructive font-heading">{stats?.expired}</p>
              <p className="text-sm text-muted-foreground">{t('certExpired')}</p>
            </div>
            <div className="bg-card rounded-lg p-4 border border-warning/20 shadow-elevation-1">
              <p className="text-2xl font-bold text-warning font-heading">{stats?.expiringSoon}</p>
              <p className="text-sm text-muted-foreground">{t('certExpiringSoon')}</p>
            </div>
            <div className="bg-card rounded-lg p-4 border border-success/20 shadow-elevation-1">
              <p className="text-2xl font-bold text-success font-heading">{stats?.valid}</p>
              <p className="text-sm text-muted-foreground">{t('certValid')}</p>
            </div>
          </div>

          {/* Expiry Warning Banners */}
          <ExpiryWarningBanner certifications={certifications} />

          {/* Filters */}
          <div className="bg-card rounded-lg p-4 border border-border shadow-elevation-1 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={t('certSearchPlaceholder')}
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              <select
                value={filterType}
                onChange={e => setFilterType(e?.target?.value)}
                className="rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {CERT_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {EXPIRY_STATUSES?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
              <Icon name="AlertCircle" size={18} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Desktop Table */}
          <div className="hidden md:block bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColWorker')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColCertType')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColCertNumber')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColExpiryDate')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColStatus')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('certColDocument')}</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredCerts?.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-16">
                        <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                        <p className="text-muted-foreground font-medium">{t('certNoneFound')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t('certNoneFoundDesc')}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredCerts?.map(cert => (
                      <tr key={cert?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-foreground text-sm">{cert?.worker?.full_name || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">{cert?.worker?.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-accent/10 text-accent">
                            {cert?.certificate_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {cert?.certificate_number || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {workerCertificationService?.formatExpiryDate(cert?.expiry_date)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge expiryDate={cert?.expiry_date} />
                        </td>
                        <td className="px-4 py-3">
                          {cert?.file_path ? (
                            <button
                              onClick={() => handleViewFile(cert)}
                              className="flex items-center gap-1.5 text-sm text-accent hover:underline"
                            >
                              <Icon name="FileText" size={14} />
                              {t('view')}
                            </button>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditingCert(cert)}
                              disabled={subscriptionReadOnly}
                              className="p-1.5 rounded-lg hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title={t('edit')}
                            >
                              <Icon name="Pencil" size={15} color="var(--color-accent)" />
                            </button>
                            <button
                              onClick={() => handleDelete(cert?.id)}
                              disabled={deletingId === cert?.id || subscriptionReadOnly}
                              className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t('delete')}
                            >
                              <Icon name="Trash2" size={15} color="var(--color-destructive)" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card Layout */}
          <div className="md:hidden space-y-3">
            {filteredCerts?.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-lg border border-border">
                <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-muted-foreground">{t('certNoneFound')}</p>
              </div>
            ) : (
              filteredCerts?.map(cert => (
                <div key={cert?.id} className="bg-card rounded-lg border border-border p-4 shadow-elevation-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-foreground">{cert?.worker?.full_name || 'N/A'}</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent/10 text-accent mt-1">
                        {cert?.certificate_type}
                      </span>
                    </div>
                    <StatusBadge expiryDate={cert?.expiry_date} />
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground mb-3">
                    {cert?.certificate_number && (
                      <p>{t('certColCertNumber')}: <span className="text-foreground">{cert?.certificate_number}</span></p>
                    )}
                    <p>{t('certColExpiryDate')}: <span className="text-foreground font-medium">{workerCertificationService?.formatExpiryDate(cert?.expiry_date)}</span></p>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    {cert?.file_path ? (
                      <button onClick={() => handleViewFile(cert)} className="flex items-center gap-1.5 text-sm text-accent">
                        <Icon name="FileText" size={14} />
                        {t('certViewDocument')}
                      </button>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingCert(cert)}
                        disabled={subscriptionReadOnly}
                        className="p-2 rounded-lg hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Icon name="Pencil" size={16} color="var(--color-accent)" />
                      </button>
                      <button
                        onClick={() => handleDelete(cert?.id)}
                        disabled={deletingId === cert?.id || subscriptionReadOnly}
                        className="p-2 rounded-lg hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Results count */}
          {filteredCerts?.length > 0 && (
            <p className="text-sm text-muted-foreground mt-4">
              {t('certShowingCount')?.replace('{shown}', filteredCerts?.length)?.replace('{total}', certifications?.length)}
            </p>
          )}
        </div>
      </main>
      {/* Add Modal */}
      {showAddModal && (
        <AddCertificationModal
          companyId={company?.id}
          onClose={() => setShowAddModal(false)}
          onSaved={loadData}
        />
      )}
      {/* Edit Modal */}
      {editingCert && (
        <EditCertificationModal
          certification={editingCert}
          onClose={() => setEditingCert(null)}
          onSaved={loadData}
        />
      )}
    </div>
  );
};

export default WorkerCertificationsManagement;
